import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const url = error.config?.url || '';
      if (!url.includes('/auth/login') && !url.includes('/auth/register') && !url.includes('/auth/me')) {
        sessionStorage.removeItem('eva_user');
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

export const authService = {
  login: async (email, password, rester_connecte) => {
    const response = await api.post('/auth/login', { email, password, rester_connecte });
    if (response.data.user) {
      sessionStorage.setItem('eva_user', JSON.stringify(response.data.user));
    }
    return response.data.user;
  },
  
  register: async (email, password, nom, prenom) => {
    const response = await api.post('/auth/register', { email, password, nom, prenom });
    if (response.data.user) {
      sessionStorage.setItem('eva_user', JSON.stringify(response.data.user));
    }
    return response.data;
  },
  
  logout: async () => {
    try { await api.post('/auth/logout'); } catch {}
    sessionStorage.removeItem('eva_user');
    window.location.href = '/login';
  },
  
  getCurrentUser: () => {
    const user = sessionStorage.getItem('eva_user');
    return user ? JSON.parse(user) : null;
  },
  
  isAuthenticated: () => {
    return !!sessionStorage.getItem('eva_user');
  },

  refreshUser: async () => {
    try {
      const response = await api.get('/auth/me');
      sessionStorage.setItem('eva_user', JSON.stringify(response.data));
      return response.data;
    } catch {
      sessionStorage.removeItem('eva_user');
      return null;
    }
  },
  
  forgotPassword: async (email) => {
    return await api.post('/auth/forgot-password', { email });
  }
};

export const questionnaireService = {
  generateGeneric: async (fonction, niveau, competences) => {
    return await api.post('/questionnaires/generate-generic', { fonction, niveau, competences });
  },
  
  generateFromDescription: async (description, file) => {
    const formData = new FormData();
    formData.append('description', description);
    if (file) {
      formData.append('file', file);
    }
    return await api.post('/questionnaires/generate-from-description', formData);
  },
  
  regenerateQuestion: async (questionIndex, context) => {
    return await api.post('/questionnaires/regenerate-question', { question_index: questionIndex, context });
  },
  
  save: async (data) => {
    return await api.post('/questionnaires', data);
  },
  
  getAll: async (filters = {}) => {
    return await api.get('/questionnaires', { params: filters });
  },
  
  getById: async (id) => {
    return await api.get(`/questionnaires/${id}`);
  },
  
  getStats: async (id) => {
    return await api.get(`/questionnaires/${id}/stats`);
  },
  
  getAllQuestions: async (filters = {}) => {
    return await api.get('/questions', { params: filters });
  }
};

export const testService = {
  create: async (questionnaire_id, nom_candidat, prenom_candidat) => {
    return await api.post('/tests', { questionnaire_id, nom_candidat, prenom_candidat });
  },
  
  answerQuestion: async (testId, questionIndex, rating, checkedAnswers = [], selectedChoices = []) => {
    return await api.put(`/tests/${testId}/answer`, { 
      question_index: questionIndex, 
      rating,
      checked_answers: checkedAnswers,
      selected_choices: selectedChoices
    });
  },
  
  complete: async (testId) => {
    return await api.post(`/tests/${testId}/complete`);
  },
  
  getById: async (testId) => {
    return await api.get(`/tests/${testId}`);
  },
  
  getRadarData: async (testId) => {
    return await api.get(`/tests/${testId}/radar-data`);
  },
  
  compare: async (testIds) => {
    return await api.post('/tests/compare', { test_ids: testIds });
  },
  
  getAll: async (filters = {}) => {
    return await api.get('/tests', { params: filters });
  },
  
  exportPdf: async (testId, anonyme = false, format = 'complet') => {
    let params = `?format=${format}`;
    if (anonyme) params += '&anonyme=true';
    const url = `${API_BASE}/tests/${testId}/export-pdf${params}`;
    const response = await fetch(url, {
      credentials: 'include'
    });
    if (!response.ok) throw new Error('Export failed');
    const disposition = response.headers.get('Content-Disposition') || '';
    let filename = `rapport_test_${testId}.pdf`;
    const match = disposition.match(/filename\*=UTF-8''(.+)/);
    if (match) {
      filename = decodeURIComponent(match[1]);
    }
    const blob = await response.blob();
    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(blobUrl);
  },
};

export const adminService = {
  updateTest: async (testId, data) => {
    return await api.put(`/admin/tests/${testId}`, data);
  },
  deleteTest: async (testId) => {
    return await api.delete(`/admin/tests/${testId}`);
  },
  updateQuestionnaire: async (id, data) => {
    return await api.put(`/admin/questionnaires/${id}`, data);
  },
  deleteQuestionnaire: async (id) => {
    return await api.delete(`/admin/questionnaires/${id}`);
  },
  checkDuplicateRefAo: async (refAo) => {
    return await api.get('/questionnaires/check-duplicate', { params: { ref_ao: refAo } });
  },
};

export const dashboardService = {
  getStats: async (filters = {}) => {
    return await api.get('/dashboard/stats', { params: filters });
  },
  exportPdf: async (filters = {}) => {
    const query = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v) query.append(k, v); });
    const qs = query.toString();
    const url = `${API_BASE}/dashboard/export-pdf${qs ? `?${qs}` : ''}`;
    const response = await fetch(url, { credentials: 'include' });
    if (!response.ok) throw new Error('Export failed');
    const disposition = response.headers.get('Content-Disposition') || '';
    let filename = 'tableau-de-bord-eva.pdf';
    const match = disposition.match(/filename\*=UTF-8''(.+)/);
    if (match) filename = decodeURIComponent(match[1]);
    const blob = await response.blob();
    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(blobUrl);
  }
};

export const clientService = {
  getAll: async () => {
    return await api.get('/clients');
  },
  
  create: async (name) => {
    return await api.post('/clients', null, { params: { name } });
  },
  
  getContacts: async (clientId) => {
    return await api.get('/client-contacts', { params: { client_id: clientId } });
  },
  
  createContact: async (name, clientId) => {
    return await api.post('/client-contacts', null, { params: { name, client_id: clientId } });
  }
};
